import React, { useRef } from 'react';
import { uploadImageToS3 } from '../services/s3Service';
import { 
    Box, 
    Button, 
    IconButton, 
    CircularProgress, 
    alpha
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import MovieIcon from '@mui/icons-material/Movie';
import { 
    DndContext, 
    closestCenter, 
    MouseSensor, 
    TouchSensor, 
    useSensor, 
    useSensors 
} from '@dnd-kit/core';
import { 
    SortableContext, 
    rectSortingStrategy, 
    arrayMove 
} from '@dnd-kit/sortable';
import { SortableItem } from './SortableItem';

const MultiImageUpload = ({ images, onChange }) => {
    const fileInputRef = useRef(null);
    const [uploading, setUploading] = React.useState(false);

    const sensors = useSensors(
        useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
    );

    const handleFileChange = async (event) => {
        const files = Array.from(event.target.files);
        if (files.length === 0) return;

        setUploading(true);
        try {
	            const newImages = await Promise.all(files.map(async (file) => {
	                try {
	                    // Use 'news/' folder consistently
	                    const result = await uploadImageToS3(file, 'news/');
	                    return {
	                        url: result.url,
                        metadata: { name: file.name, size: file.size, key: result.key, type: file.type }
                    };
                } catch (error) {
                    console.error("Failed to upload file:", file.name, error);
                    alert(`Falha ao enviar arquivo ${file.name}: ${error.message}`);
                    return null;
                }
            }));
            
            const validImages = newImages.filter(img => img !== null);
            onChange([...(images || []), ...validImages]);
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const removeImage = (index) => {
        const updated = [...(images || [])];
        updated.splice(index, 1);
        onChange(updated);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        
        // Find indexes based on the URL (stable ID)
        const oldIndex = images.findIndex((img) => img.url === active.id);
        const newIndex = images.findIndex((img) => img.url === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
            const newLayout = arrayMove(images, oldIndex, newIndex);
            onChange(newLayout);
        }
    };

    // Helper to check if url is video
    const isVideoUrl = (url) => {
        return url.match(/\.(mp4|webm|ogg|mov)$/i) || url.includes('youtube') || url.includes('vimeo');
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ mb: 1.25 }}>
                <DndContext 
                    sensors={sensors} 
                    collisionDetection={closestCenter} 
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext 
                        // Use URL as unique ID for stability
                        items={images ? images.map(img => img.url) : []}
                        strategy={rectSortingStrategy}
                    >
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
                            {images && images.map((img, index) => (
                                <SortableItem key={img.url} id={img.url}>
                                    <Box 
                                        sx={{ 
                                            position: 'relative', 
                                            width: 120, 
                                            height: 120, 
                                            borderRadius: '16px', 
                                            overflow: 'hidden', 
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                            transition: 'all 0.3s ease',
                                            border: '1px solid rgba(0,0,0,0.05)',
                                            bgcolor: '#f5f5f5',
                                            '&:hover': { 
                                                transform: 'scale(1.02)',
                                                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                                                '& .delete-overlay': { opacity: 1 } 
                                            }
                                        }}
                                    >
                                        {isVideoUrl(img.url) ? (
                                            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#000' }}>
                                                 <MovieIcon sx={{ color: 'white', fontSize: 40, opacity: 0.7 }} />
                                            </Box>
                                        ) : (
                                            <Box 
                                                component="img"
                                                src={img.url || img.preview} 
                                                alt={`Upload ${index}`} 
                                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                                            />
                                        )}
                                        
                                        <IconButton 
                                            className="delete-overlay"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                removeImage(index);
                                            }}
                                            onMouseDown={(e) => e.stopPropagation()} 
                                            onTouchStart={(e) => e.stopPropagation()}
                                            size="small"
                                            sx={{ 
                                                position: 'absolute', 
                                                top: 4, 
                                                right: 4, 
                                                backgroundColor: alpha('#000', 0.6), 
                                                color: 'white',
                                                opacity: { xs: 1, md: 0 },
                                                transition: 'opacity 0.2s',
                                                '&:hover': {
                                                    backgroundColor: alpha('#000', 0.8),
                                                }
                                            }}
                                            title="Remover"
                                        >
                                            <DeleteIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Box>
                                </SortableItem>
                            ))}
                        </Box>
                    </SortableContext>
                </DndContext>
            </Box>
            
            <input 
                type="file" 
                multiple 
                accept="image/*,video/*" 
                onChange={handleFileChange} 
                style={{ display: 'none' }} 
                ref={fileInputRef}
            />
            <Button 
                variant="outlined"
                fullWidth
                onClick={() => fileInputRef.current.click()}
                disabled={uploading}
                startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <AddPhotoAlternateIcon />}
                sx={{ 
                    py: 2,
                    borderStyle: 'dashed',
                    borderWidth: 2,
                    borderColor: '#ddd',
                    color: '#666',
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: '12px',
                    transition: 'all 0.3s',
                    '&:hover': {
                        borderColor: '#cc0000',
                        backgroundColor: alpha('#cc0000', 0.02),
                        borderStyle: 'dashed',
                        transform: 'translateY(-1px)'
                    }
                }}
            >
                {uploading ? 'Processando...' : '+ Adicionar Fotos/Vídeos'}
            </Button>
        </Box>
    );
};

export default MultiImageUpload;
